import { type FunctionComponent, useContext, useState } from 'react';

import { UserContext } from 'App';

import i18n from 'i18n/i18n';
import { RIGHTS } from 'interfaces/Interfaces';
import { Button } from 'lib/Button';
import { evaluateContracts, resetDunningLevels } from 'services/ApiService';
import { testAttribute } from 'util/Util';

import { alertToast, showSuccessToast } from '../Toast/AlertToast';

import './ManualActionsPage.style.sass';

type PendingAction = 'evaluate' | 'reset' | null;

const ManualActionsPage: FunctionComponent = () => {
  const [contractIdsText, setContractIdsText] = useState('');
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);

  const userContext = useContext(UserContext);
  const canResetDunningLevels = userContext.rights.includes(RIGHTS.RESET_DUNNING_LEVELS);

  function parseContractIds(): number[] {
    return contractIdsText
      .split(/\r\n|\n|\r/)
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .map(Number)
      .filter(n => !Number.isNaN(n));
  }

  function runAction(
    action: Exclude<PendingAction, null>,
    request: (contractIds: number[]) => Promise<void>,
    successMessageKey: string,
  ) {
    const contractIds = parseContractIds();
    if (contractIds.length === 0 || pendingAction !== null) return;

    setPendingAction(action);
    request(contractIds)
      .then(async () => {
        setContractIdsText('');
        await showSuccessToast(i18n.t(successMessageKey));
      })
      .catch(err => alertToast(err))
      .finally(() => setPendingAction(null));
  }

  function handleEvaluate() {
    runAction('evaluate', evaluateContracts, 'manual-actions.evaluate.success');
  }

  function handleReset() {
    runAction('reset', resetDunningLevels, 'manual-actions.reset.success');
  }

  const hasContractIds = parseContractIds().length > 0;
  const isBusy = pendingAction !== null;

  return (
    <div className="manual-actions-page">
      <div className="title">{i18n.t('manual-actions.title')}</div>
      <div className="manual-actions-content">
        <textarea
          data-test-id={testAttribute('mnac', 'contract-ids.input')}
          className="contract-ids-textarea"
          placeholder={i18n.t('manual-actions.contract-ids-placeholder')}
          value={contractIdsText}
          onChange={e => setContractIdsText(e.target.value)}
          disabled={isBusy}
          style={{ backgroundColor: 'white', color: 'black' }}
        />
        <div className="manual-actions-buttons">
          <Button
            testId={testAttribute('mnac', 'evaluate.button')}
            onClick={handleEvaluate}
            disabled={isBusy || !hasContractIds}>
            {pendingAction === 'evaluate' ? i18n.t('busy') : i18n.t('manual-actions.evaluate.button')}
          </Button>
          {canResetDunningLevels && (
            <Button
              testId={testAttribute('mnac', 'reset.button')}
              onClick={handleReset}
              disabled={isBusy || !hasContractIds}>
              {pendingAction === 'reset' ? i18n.t('busy') : i18n.t('manual-actions.reset.button')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManualActionsPage;
